import { FetchResult } from 'apollo-boost';
import { ApolloQueryResult } from 'apollo-client';
import { get, toPath } from 'lodash';

import { Action } from '../../../actions';
import {
    clearSiteLinks,
    saveSiteLinksSuccess,
    showSiteLinksErrors
} from '../../../actions/banners-edit/banner-sitelinks';
import { updateBannerPriceErrors, validateBannerPriceSuccess } from '../../../actions/banners-edit/price';
import { saveSuccessVisitingCardForEditBanners } from '../../../actions/bannersEdit';
import { saveSuccessForEditorVCards, updateVCardsErrors } from '../../../actions/visitingCardEditor';
import { mapVisitingCardErrors } from '../../../mappers/banners-edit/state-data-mappers/state-data-mappers';
import {
    getBannerPriceErrorsIdx,
    getBannersErrorsSetWithIds,
    getFormattedErrors,
    sitelinksFieldErrorMapper
} from '../../../service/BannerEditErrors/BannerEditErrors';
import { convertSitelinkErrorIndex, getBannerEditPageItemEditorId } from '../../../service/BannerHelpers/BannerHelpers';
import { ApiMethodExecutingError, NotSyncedError } from '../../../service/Errors';
import { convertServerPathToClient } from '../../../service/ErrorsMapper/ErrorsMapper';
import { SiteLinkData } from '../../../typings/banners';
import { ValidationErrorsSet, ValidationResultItem } from '../../../typings/common';
import {
    AddSitelinkSets,
    AddSitelinkSets_addSitelinkSets_addedSitelinkSets,
    AddVCards,
    CurrencyCode,
    GdAddSitelinkSetInput,
    validateAdPrice
} from '../../../typings/graphql-grid';

export function getBannerPricePromiseErrorActions(bannersIds: string[]): Array<Action> {
    return [
        updateBannerPriceErrors(
            bannersIds,
            getBannersErrorsSetWithIds(bannersIds, [
                {
                    code: 'API_ERROR',
                    params: null,
                    path: ''
                }
            ])
        )
    ];
}

export function getBannerPricePromiseValidateActions(
    bannersIds: string[],
    result: ApolloQueryResult<validateAdPrice>,
    workCurrency: CurrencyCode
): Array<Action> {
    if (bannersIds.length === 0) {
        return [];
    }

    if (result.data) {
        let errorsIdx: string[] = [];
        if (result.data.validateAdPrice) {
            errorsIdx = getBannerPriceErrorsIdx(result.data.validateAdPrice.errors);
        }

        const successBannersIds = bannersIds.filter((id, index) => !errorsIdx.includes(String(index)));
        const errorsBannersIds = bannersIds.filter((id, index) => errorsIdx.includes(String(index)));

        const updateAction$ = [];

        if (result.data.validateAdPrice) {
            updateAction$.push(
                updateBannerPriceErrors(errorsBannersIds, getFormattedErrors(result.data.validateAdPrice.errors, 0))
            );
        }

        return [...updateAction$, validateBannerPriceSuccess(successBannersIds, workCurrency)];
    } else {
        return [
            updateBannerPriceErrors(
                bannersIds,
                getBannersErrorsSetWithIds(bannersIds, [
                    {
                        code: 'VALIDATE_ERROR',
                        params: null,
                        path: ''
                    }
                ])
            )
        ];
    }
}

export function getSiteLinksErrors(formattedErrors: ValidationErrorsSet, newSiteLinks: GdAddSitelinkSetInput[]) {
    return Object.keys(formattedErrors).map((id: string) => {
        return convertServerPathToClient(formattedErrors[id], sitelinksFieldErrorMapper).map(
            (err: ValidationResultItem): ValidationResultItem => {
                const pathArr = toPath(err.path).slice(2); // ужасный костыль, обрезаем у сайтлинковых путей 'set.0.'
                const sitelinkErrorIndex = Number(pathArr[1]);

                // если всё-таки удалось отправить запросы на бэк с невалидными данными в обход клиентской валидации,
                // то возможны случаи, когда индекса нет, обходим это
                if (sitelinkErrorIndex) {
                    const sitelinkIndex: number = convertSitelinkErrorIndex(
                        sitelinkErrorIndex,
                        newSiteLinks[id].sitelinks
                    );

                    pathArr[1] = String(sitelinkIndex);
                }

                return {
                    ...err,
                    path: pathArr.join('.')
                };
            }
        );
    });
}

export function getSiteLinksPromiseErrorActions(
    error: ApiMethodExecutingError,
    bannersIds: string[],
    collapsedSiteLinks: Array<Array<SiteLinkData>>,
    newSiteLinks: GdAddSitelinkSetInput[],
    login: string
): Array<Action> {
    if (error instanceof NotSyncedError) {
        const validationErrors = get(error.data, 'data.addSitelinkSets.validationResult.errors', []);

        const formattedErrors = getFormattedErrors(validationErrors, 1);

        const sitelinksSet: string[] = error.data.data.addSitelinkSets.addedSitelinkSets.map(
            (item: AddSitelinkSets_addSitelinkSets_addedSitelinkSets | null) => item && item.id
        ) as string[];

        const successIds = bannersIds.filter((id, index) => !Boolean(formattedErrors[index]));
        const errorsIds = bannersIds.filter(
            (id, index) => Boolean(formattedErrors[index]) && newSiteLinks[index].sitelinks.length !== 0
        );
        const emptyIds = bannersIds.filter((id, index) => newSiteLinks[index].sitelinks.length === 0);

        const errors = getSiteLinksErrors(formattedErrors, newSiteLinks);

        return [
            saveSiteLinksSuccess(sitelinksSet, collapsedSiteLinks, successIds),
            showSiteLinksErrors(errorsIds, collapsedSiteLinks, errors),
            clearSiteLinks(emptyIds)
        ];
    }

    return [
        showSiteLinksErrors(
            bannersIds,
            collapsedSiteLinks,
            bannersIds.map(() => [
                {
                    code: 'API_ERROR',
                    params: null,
                    path: ''
                }
            ])
        )
    ];
}

export function getSiteLinksPromiseSaveActions(
    bannersIds: string[],
    newSiteLinks: GdAddSitelinkSetInput[],
    login: string,
    response: FetchResult<AddSitelinkSets>,
    collapsedSiteLinks: Array<Array<SiteLinkData>>
): Array<Action> {
    if (bannersIds.length === 0) {
        return [];
    }

    if (response.errors && response.errors.length === 0 && response.data) {
        const validationErrors = get(response, 'data.addSitelinkSets.validationResult.errors', []);

        const formattedErrors = getFormattedErrors(validationErrors, 1);

        const sitelinksSet: string[] = response.data.addSitelinkSets.addedSitelinkSets.map(
            item => item && item.id
        ) as string[];

        const successIds = bannersIds.filter((id, index) => !Boolean(formattedErrors[index]));
        const errorsIds = bannersIds.filter(
            (id, index) => Boolean(formattedErrors[index]) && newSiteLinks[index].sitelinks.length !== 0
        );
        const emptyIds = bannersIds.filter((id, index) => newSiteLinks[index].sitelinks.length === 0);

        const errors = getSiteLinksErrors(formattedErrors, newSiteLinks);

        return [
            saveSiteLinksSuccess(sitelinksSet, collapsedSiteLinks, successIds),
            showSiteLinksErrors(errorsIds, collapsedSiteLinks, errors),
            clearSiteLinks(emptyIds)
        ];
    } else {
        return [
            showSiteLinksErrors(
                bannersIds,
                collapsedSiteLinks,
                bannersIds.map(() => [
                    {
                        code: 'API_ERROR',
                        params: null,
                        path: ''
                    }
                ])
            )
        ];
    }
}

export function getVCardsPromiseErrorActions(bannerIds: string[]): Array<Action> {
    return [
        updateBannerPriceErrors(
            bannerIds,
            getBannersErrorsSetWithIds(bannerIds, [
                {
                    code: 'API_ERROR',
                    params: null,
                    path: ''
                }
            ])
        )
    ];
}

export function getVCardsPromiseSaveActions(bannerIds: string[], result: FetchResult<AddVCards>): Array<Action> {
    if (bannerIds.length === 0 || !result.data) {
        return [];
    }

    const responseErrors = result.errors || [];
    const validationErrors = get(result, 'data.addVcards.validationResult.errors', []);
    const formattedErrors = getFormattedErrors(validationErrors, 1);
    const vCardsIds: string[] = result.data.addVcards.addedVcards.map(item => item && item.id) as string[];
    const editorIds = bannerIds.map(getBannerEditPageItemEditorId);

    const errorsEditorIds = editorIds.filter((id, index) => Boolean(formattedErrors[index]));

    if (responseErrors.length === 0 && result.data) {
        if (validationErrors.length && errorsEditorIds.length) {
            const formattedVisitingCardErrors = mapVisitingCardErrors(formattedErrors);
            return [updateVCardsErrors(editorIds, errorsEditorIds, formattedVisitingCardErrors)];
        }

        return [
            saveSuccessVisitingCardForEditBanners(bannerIds, vCardsIds),
            saveSuccessForEditorVCards(editorIds, vCardsIds)
        ];
    } else {
        const commonFormattedErrors: Record<string, ValidationResultItem[]> = editorIds.reduce(
            (acc, editorId, index) => {
                acc[index] = [
                    {
                        code: 'VALIDATE_ERROR',
                        params: null,
                        path: ''
                    }
                ];

                return acc;
            },
            {}
        );
        return [updateVCardsErrors(editorIds, errorsEditorIds, mapVisitingCardErrors(commonFormattedErrors))];
    }
}
